import React from 'react';
import { Search, Eye, Edit, Plus, RefreshCw, User, Phone, MapPin, CreditCard, ImageIcon } from 'lucide-react';

function personInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function DetailField({ icon: Icon, label, value, children }) {
  return (
    <div className="jet-crm-detail-field">
      <div className="jet-crm-detail-field__label">
        {Icon ? <Icon className="w-4 h-4" aria-hidden /> : null}
        <span>{label}</span>
      </div>
      <div className="jet-crm-detail-field__value">{children ?? (value || '—')}</div>
    </div>
  );
}

function ImageUploadField({ label, previewUrl, onChoose, inputId }) {
  return (
    <div className="jet-crm-upload">
      <span className="jet-crm-form-label">{label}</span>
      <div className="jet-crm-upload__row">
        <div className="jet-crm-upload__preview">
          {previewUrl ? (
            <img src={previewUrl} alt="" />
          ) : (
            <ImageIcon className="w-5 h-5" style={{ opacity: 0.35 }} aria-hidden />
          )}
        </div>
        <label htmlFor={inputId} className="jet-btn jet-btn-secondary jet-crm-upload__btn">
          Choose file
        </label>
        <input id={inputId} type="file" accept="image/*" onChange={onChoose} className="jet-crm-upload__input" />
      </div>
    </div>
  );
}

function ShipperList({
  items,
  loading,
  emptyAll,
  selectedPassport,
  onSelect,
  onEdit,
}) {
  if (loading) {
    return <div className="jet-crm-empty"><p>Loading shippers…</p></div>;
  }
  if (emptyAll) {
    return (
      <div className="jet-crm-empty">
        <User className="w-10 h-10" style={{ opacity: 0.25 }} />
        <p><strong>No shippers yet</strong></p>
        <p className="jet-crm-empty__hint">Add a shipper using the button on the right.</p>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="jet-crm-empty">
        <Search className="w-8 h-8" style={{ opacity: 0.25 }} />
        <p>No shippers match your search.</p>
      </div>
    );
  }
  return (
    <ul className="jet-crm-list" role="listbox" aria-label="Shippers">
      {items.map((shipper) => {
        const selected = selectedPassport && shipper.passportNo === selectedPassport;
        return (
          <li key={shipper._id || shipper.passportNo}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              className={`jet-crm-list-item${selected ? ' is-selected' : ''}`}
              onClick={() => onSelect(shipper)}
            >
              <span className="jet-crm-avatar" aria-hidden>{personInitials(shipper.nameWithInitials)}</span>
              <span className="jet-crm-list-item__main">
                <span className="jet-crm-list-item__name">{shipper.nameWithInitials || 'Unnamed shipper'}</span>
                <span className="jet-crm-list-item__sub">{shipper.passportNo || 'No passport'}</span>
              </span>
              <span className="jet-crm-list-item__actions">
                <button
                  type="button"
                  className="jet-btn jet-btn-icon jet-btn-secondary"
                  title="View"
                  aria-label="View shipper"
                  onClick={(e) => { e.stopPropagation(); onSelect(shipper); }}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="jet-btn jet-btn-icon jet-btn-primary"
                  title="Edit"
                  aria-label="Edit shipper"
                  onClick={(e) => { e.stopPropagation(); onEdit(shipper); }}
                >
                  <Edit className="w-4 h-4" />
                </button>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ConsigneeList({
  items,
  loading,
  emptyAll,
  selectedConsignee,
  consigneeMatchesId,
  consigneeIdLabel,
  onSelect,
  onEdit,
}) {
  if (loading) {
    return <div className="jet-crm-empty"><p>Loading consignees…</p></div>;
  }
  if (emptyAll) {
    return (
      <div className="jet-crm-empty">
        <User className="w-10 h-10" style={{ opacity: 0.25 }} />
        <p><strong>No consignees yet</strong></p>
        <p className="jet-crm-empty__hint">Add a consignee using the button on the right.</p>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="jet-crm-empty">
        <Search className="w-8 h-8" style={{ opacity: 0.25 }} />
        <p>No consignees match your search.</p>
      </div>
    );
  }
  return (
    <ul className="jet-crm-list" role="listbox" aria-label="Consignees">
      {items.map((consignee) => {
        const selected = selectedConsignee && (
          (consignee._id && selectedConsignee._id && String(consignee._id) === String(selectedConsignee._id))
          || consigneeMatchesId(consignee, consigneeIdLabel(selectedConsignee))
        );
        return (
          <li key={consignee._id || consignee.passportNo || consignee.nicNo}>
            <button
              type="button"
              role="option"
              aria-selected={selected}
              className={`jet-crm-list-item${selected ? ' is-selected' : ''}`}
              onClick={() => onSelect(consignee)}
            >
              <span className="jet-crm-avatar jet-crm-avatar--consignee" aria-hidden>
                {personInitials(consignee.nameWithInitials)}
              </span>
              <span className="jet-crm-list-item__main">
                <span className="jet-crm-list-item__name">{consignee.nameWithInitials || 'Unnamed consignee'}</span>
                <span className="jet-crm-list-item__sub">{consigneeIdLabel(consignee)}</span>
              </span>
              <span className="jet-crm-list-item__actions">
                <button
                  type="button"
                  className="jet-btn jet-btn-icon jet-btn-secondary"
                  title="View"
                  aria-label="View consignee"
                  onClick={(e) => { e.stopPropagation(); onSelect(consignee); }}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  className="jet-btn jet-btn-icon jet-btn-primary"
                  title="Edit"
                  aria-label="Edit consignee"
                  onClick={(e) => { e.stopPropagation(); onEdit(consignee); }}
                >
                  <Edit className="w-4 h-4" />
                </button>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function AdminCustomersPanel({
  customerTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  onClearSearch,
  shippersList,
  filteredShippersList,
  loadingShippers,
  onRefreshShippers,
  selectedShipperCustomer,
  shipperForm,
  setShipperForm,
  onViewShipper,
  onEditShipper,
  onResetShipper,
  onSaveShipper,
  savingShipper,
  consigneesList,
  filteredConsigneesList,
  loadingConsignees,
  onRefreshConsignees,
  selectedConsigneeCustomer,
  consigneeForm,
  setConsigneeForm,
  onViewConsignee,
  onEditConsignee,
  onResetConsignee,
  onSaveConsignee,
  savingConsignee,
  customerPanelMode,
  normalizeMobiles,
  getPreviewUrl,
  consigneeIdLabel,
  idTypeLabel,
  consigneeMatchesId,
  onImageChoose,
}) {
  const isShippers = customerTab === 'shippers';
  const listCount = isShippers ? filteredShippersList.length : filteredConsigneesList.length;
  const totalCount = isShippers ? shippersList.length : consigneesList.length;

  const panelTitle = (() => {
    if (customerPanelMode === 'view') return 'Record details';
    if (customerPanelMode === 'edit') return isShippers ? 'Edit shipper' : 'Edit consignee';
    return isShippers ? 'New shipper' : 'New consignee';
  })();

  const handleRefresh = () => {
    if (isShippers) onRefreshShippers();
    else onRefreshConsignees();
  };

  const handleAddNew = () => {
    if (isShippers) onResetShipper();
    else onResetConsignee();
  };

  return (
    <div className="jet-crm">
      <header className="jet-crm-header">
        <div>
          <h2 className="jet-crm-title">Consignees &amp; shippers</h2>
          <p className="jet-crm-subtitle">Manage customer records used on collections and documents.</p>
        </div>
        <div className="jet-crm-header__actions">
          <button type="button" className="jet-btn jet-btn-secondary" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button type="button" className="jet-btn jet-btn-primary" onClick={handleAddNew}>
            <Plus className="w-4 h-4" />
            {isShippers ? 'Add shipper' : 'Add consignee'}
          </button>
        </div>
      </header>

      <div className="jet-crm-segments" role="tablist" aria-label="Customer type">
        <button
          type="button"
          role="tab"
          aria-selected={isShippers}
          className={`jet-crm-segment${isShippers ? ' is-active' : ''}`}
          onClick={() => onTabChange('shippers')}
        >
          Shippers
          <span className="jet-crm-segment__count">{shippersList.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isShippers}
          className={`jet-crm-segment${!isShippers ? ' is-active' : ''}`}
          onClick={() => onTabChange('consignees')}
        >
          Consignees
          <span className="jet-crm-segment__count">{consigneesList.length}</span>
        </button>
      </div>

      <div className="jet-crm-toolbar">
        <div className="jet-crm-search">
          <Search className="jet-crm-search__icon w-4 h-4" aria-hidden />
          <input
            type="search"
            className="jet-crm-search__input"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={isShippers ? 'Search by name, passport, or address…' : 'Search by name, ID, or address…'}
            aria-label="Search customers"
          />
        </div>
        {searchQuery ? (
          <button type="button" className="jet-btn jet-btn-ghost" onClick={onClearSearch}>
            Clear
          </button>
        ) : null}
      </div>

      <div className="jet-crm-layout">
        <section className="jet-crm-list-pane" aria-label={isShippers ? 'Shipper list' : 'Consignee list'}>
          <div className="jet-crm-list-pane__head">
            <h3>{isShippers ? 'Shippers' : 'Consignees'}</h3>
            <span className="jet-crm-count-badge">
              {listCount}
              {listCount !== totalCount ? ` / ${totalCount}` : ''}
            </span>
          </div>
          <div className="jet-crm-list-scroll">
            {isShippers ? (
              <ShipperList
                items={filteredShippersList}
                loading={loadingShippers}
                emptyAll={shippersList.length === 0}
                selectedPassport={selectedShipperCustomer?.passportNo}
                onSelect={(s) => onViewShipper(s, { closePreview: false })}
                onEdit={onEditShipper}
              />
            ) : (
              <ConsigneeList
                items={filteredConsigneesList}
                loading={loadingConsignees}
                emptyAll={consigneesList.length === 0}
                selectedConsignee={selectedConsigneeCustomer}
                consigneeMatchesId={consigneeMatchesId}
                consigneeIdLabel={consigneeIdLabel}
                onSelect={(c) => onViewConsignee(c, { closePreview: false })}
                onEdit={onEditConsignee}
              />
            )}
          </div>
        </section>

        <aside className="jet-crm-panel" aria-label={panelTitle}>
          <header className="jet-crm-panel__head">
            <div>
              <p className="jet-crm-panel__eyebrow">{isShippers ? 'Shipper' : 'Consignee'}</p>
              <h3 className="jet-crm-panel__title">{panelTitle}</h3>
            </div>
            <div className="jet-crm-panel__head-actions">
              {customerPanelMode === 'view' && (isShippers ? selectedShipperCustomer : selectedConsigneeCustomer) ? (
                <>
                  <button
                    type="button"
                    className="jet-btn jet-btn-primary"
                    onClick={() => (isShippers ? onEditShipper(selectedShipperCustomer) : onEditConsignee(selectedConsigneeCustomer))}
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={handleAddNew}>
                    <Plus className="w-4 h-4" />
                    New
                  </button>
                </>
              ) : customerPanelMode === 'edit' ? (
                <button
                  type="button"
                  className="jet-btn jet-btn-secondary"
                  onClick={() => (isShippers
                    ? onViewShipper(selectedShipperCustomer, { closePreview: false })
                    : onViewConsignee(selectedConsigneeCustomer, { closePreview: false }))}
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
              ) : null}
            </div>
          </header>

          <div className="jet-crm-panel__body">
            {isShippers ? (
              customerPanelMode === 'view' && !selectedShipperCustomer ? (
                <div className="jet-crm-empty jet-crm-panel-empty">
                  <Eye className="w-8 h-8" style={{ opacity: 0.25 }} />
                  <p><strong>Select a shipper</strong></p>
                  <p className="jet-crm-empty__hint">Choose a record from the list or click Add shipper.</p>
                </div>
              ) : customerPanelMode === 'view' && selectedShipperCustomer ? (
                <div className="jet-crm-details">
                  <DetailField icon={User} label="Name" value={selectedShipperCustomer.nameWithInitials} />
                  <DetailField icon={CreditCard} label="Passport no." value={selectedShipperCustomer.passportNo} />
                  <DetailField icon={MapPin} label="Address" value={selectedShipperCustomer.address} />
                  <DetailField icon={Phone} label="Mobile" value={normalizeMobiles(selectedShipperCustomer.mobiles).join(', ')} />
                  {selectedShipperCustomer.passportPhotoUrl ? (
                    <DetailField icon={ImageIcon} label="Passport copy">
                      <img
                        src={getPreviewUrl(selectedShipperCustomer.passportPhotoUrl, selectedShipperCustomer._id)}
                        alt="Shipper passport"
                        className="jet-crm-detail-photo"
                      />
                    </DetailField>
                  ) : null}
                </div>
              ) : (
                <form
                  className="jet-crm-form"
                  onSubmit={(e) => { e.preventDefault(); onSaveShipper(); }}
                >
                  <div className="jet-crm-form-grid">
                    <label className="jet-crm-field jet-crm-field--full">
                      <span className="jet-crm-form-label">Name with initials</span>
                      <input
                        className="form-input"
                        value={shipperForm.nameWithInitials}
                        onChange={(e) => setShipperForm((p) => ({ ...p, nameWithInitials: e.target.value }))}
                      />
                    </label>
                    <label className="jet-crm-field jet-crm-field--full">
                      <span className="jet-crm-form-label">Passport no. *</span>
                      <input
                        className="form-input"
                        value={shipperForm.passportNo}
                        onChange={(e) => setShipperForm((p) => ({ ...p, passportNo: e.target.value }))}
                        required
                      />
                    </label>
                    <label className="jet-crm-field jet-crm-field--full">
                      <span className="jet-crm-form-label">Address</span>
                      <textarea
                        className="form-textarea"
                        rows={3}
                        value={shipperForm.address}
                        onChange={(e) => setShipperForm((p) => ({ ...p, address: e.target.value }))}
                      />
                    </label>
                    <label className="jet-crm-field jet-crm-field--full">
                      <span className="jet-crm-form-label">Mobile numbers</span>
                      <input
                        className="form-input"
                        value={shipperForm.mobilesText}
                        onChange={(e) => setShipperForm((p) => ({ ...p, mobilesText: e.target.value }))}
                        placeholder="Separate with commas"
                      />
                    </label>
                    <div className="jet-crm-field jet-crm-field--full">
                      <ImageUploadField
                        label="Passport copy (1 side)"
                        inputId="shipper-passport-upload"
                        previewUrl={(shipperForm.passportPhoto || shipperForm.passportPhotoUrl)
                          ? getPreviewUrl(shipperForm.passportPhoto || shipperForm.passportPhotoUrl)
                          : ''}
                        onChoose={(e) => onImageChoose('shipper', 'passportPhoto', e.target.files?.[0])}
                      />
                    </div>
                  </div>
                  <footer className="jet-crm-form-footer">
                    <button type="submit" className="jet-btn jet-btn-primary" disabled={savingShipper}>
                      {savingShipper ? 'Saving…' : (customerPanelMode === 'edit' ? 'Save changes' : 'Create shipper')}
                    </button>
                    <button type="button" className="jet-btn jet-btn-secondary" onClick={onResetShipper}>
                      Clear form
                    </button>
                  </footer>
                </form>
              )
            ) : customerPanelMode === 'view' && !selectedConsigneeCustomer ? (
              <div className="jet-crm-empty jet-crm-panel-empty">
                <Eye className="w-8 h-8" style={{ opacity: 0.25 }} />
                <p><strong>Select a consignee</strong></p>
                <p className="jet-crm-empty__hint">Choose a record from the list or click Add consignee.</p>
              </div>
            ) : customerPanelMode === 'view' && selectedConsigneeCustomer ? (
              <div className="jet-crm-details">
                <DetailField icon={User} label="Name" value={selectedConsigneeCustomer.nameWithInitials} />
                <DetailField icon={CreditCard} label="ID type" value={idTypeLabel(selectedConsigneeCustomer.idType)} />
                <DetailField icon={CreditCard} label="ID number" value={consigneeIdLabel(selectedConsigneeCustomer)} />
                <DetailField icon={MapPin} label="Address" value={selectedConsigneeCustomer.address} />
                <DetailField icon={Phone} label="Mobile" value={normalizeMobiles(selectedConsigneeCustomer.mobiles).join(', ')} />
                {selectedConsigneeCustomer.passportPhotoUrl ? (
                  <DetailField icon={ImageIcon} label="Passport copy">
                    <img src={getPreviewUrl(selectedConsigneeCustomer.passportPhotoUrl, selectedConsigneeCustomer._id)} alt="" className="jet-crm-detail-photo" />
                  </DetailField>
                ) : null}
                {selectedConsigneeCustomer.nicFrontUrl ? (
                  <DetailField icon={ImageIcon} label="NIC front">
                    <img src={getPreviewUrl(selectedConsigneeCustomer.nicFrontUrl, selectedConsigneeCustomer._id)} alt="" className="jet-crm-detail-photo" />
                  </DetailField>
                ) : null}
                {selectedConsigneeCustomer.nicBackUrl ? (
                  <DetailField icon={ImageIcon} label="NIC back">
                    <img src={getPreviewUrl(selectedConsigneeCustomer.nicBackUrl, selectedConsigneeCustomer._id)} alt="" className="jet-crm-detail-photo" />
                  </DetailField>
                ) : null}
                {selectedConsigneeCustomer.drivingLicencePhotoUrl ? (
                  <DetailField icon={ImageIcon} label="Driving licence">
                    <img src={getPreviewUrl(selectedConsigneeCustomer.drivingLicencePhotoUrl, selectedConsigneeCustomer._id)} alt="" className="jet-crm-detail-photo" />
                  </DetailField>
                ) : null}
              </div>
            ) : (
              <form
                className="jet-crm-form"
                onSubmit={(e) => { e.preventDefault(); onSaveConsignee(); }}
              >
                <div className="jet-crm-form-grid">
                  <label className="jet-crm-field jet-crm-field--full">
                    <span className="jet-crm-form-label">Name with initials</span>
                    <input
                      className="form-input"
                      value={consigneeForm.nameWithInitials}
                      onChange={(e) => setConsigneeForm((p) => ({ ...p, nameWithInitials: e.target.value }))}
                    />
                  </label>
                  <label className="jet-crm-field">
                    <span className="jet-crm-form-label">ID type</span>
                    <select
                      className="form-input"
                      value={consigneeForm.idType}
                      onChange={(e) => setConsigneeForm((p) => ({ ...p, idType: e.target.value }))}
                    >
                      <option value="passport">Passport</option>
                      <option value="nic">NIC</option>
                      <option value="driving_licence">Driving licence</option>
                    </select>
                  </label>
                  {consigneeForm.idType === 'passport' && (
                    <label className="jet-crm-field">
                      <span className="jet-crm-form-label">Passport no.</span>
                      <input className="form-input" value={consigneeForm.passportNo} onChange={(e) => setConsigneeForm((p) => ({ ...p, passportNo: e.target.value }))} />
                    </label>
                  )}
                  {consigneeForm.idType === 'nic' && (
                    <label className="jet-crm-field">
                      <span className="jet-crm-form-label">NIC no.</span>
                      <input className="form-input" value={consigneeForm.nicNo} onChange={(e) => setConsigneeForm((p) => ({ ...p, nicNo: e.target.value }))} />
                    </label>
                  )}
                  {consigneeForm.idType === 'driving_licence' && (
                    <label className="jet-crm-field">
                      <span className="jet-crm-form-label">Driving licence no.</span>
                      <input className="form-input" value={consigneeForm.drivingLicenceNo} onChange={(e) => setConsigneeForm((p) => ({ ...p, drivingLicenceNo: e.target.value }))} />
                    </label>
                  )}
                  <label className="jet-crm-field jet-crm-field--full">
                    <span className="jet-crm-form-label">Address</span>
                    <textarea className="form-textarea" rows={3} value={consigneeForm.address} onChange={(e) => setConsigneeForm((p) => ({ ...p, address: e.target.value }))} />
                  </label>
                  <div className="jet-crm-field jet-crm-field--full">
                    <span className="jet-crm-form-label">Mobile numbers</span>
                    {(consigneeForm.mobiles || []).map((m, idx) => (
                      <input
                        key={`consignee-mobile-${idx}`}
                        className="form-input jet-crm-mobile-input"
                        value={m}
                        onChange={(e) => setConsigneeForm((p) => {
                          const mobiles = [...(p.mobiles || [''])];
                          mobiles[idx] = e.target.value;
                          return { ...p, mobiles };
                        })}
                        placeholder={`Mobile ${idx + 1}`}
                      />
                    ))}
                    <button
                      type="button"
                      className="jet-btn jet-btn-ghost jet-crm-add-mobile"
                      onClick={() => setConsigneeForm((p) => ({ ...p, mobiles: [...(p.mobiles || ['']), ''] }))}
                    >
                      <Plus className="w-4 h-4" />
                      Add mobile
                    </button>
                  </div>
                  {consigneeForm.idType === 'passport' && (
                    <div className="jet-crm-field jet-crm-field--full">
                      <ImageUploadField
                        label="Passport copy"
                        inputId="consignee-passport-upload"
                        previewUrl={(consigneeForm.passportPhoto || consigneeForm.passportPhotoUrl) ? getPreviewUrl(consigneeForm.passportPhoto || consigneeForm.passportPhotoUrl) : ''}
                        onChoose={(e) => onImageChoose('consignee', 'passportPhoto', e.target.files?.[0])}
                      />
                    </div>
                  )}
                  {consigneeForm.idType === 'driving_licence' && (
                    <div className="jet-crm-field jet-crm-field--full">
                      <ImageUploadField
                        label="Driving licence copy"
                        inputId="consignee-dl-upload"
                        previewUrl={(consigneeForm.drivingLicencePhoto || consigneeForm.drivingLicencePhotoUrl) ? getPreviewUrl(consigneeForm.drivingLicencePhoto || consigneeForm.drivingLicencePhotoUrl) : ''}
                        onChoose={(e) => onImageChoose('consignee', 'drivingLicencePhoto', e.target.files?.[0])}
                      />
                    </div>
                  )}
                  {consigneeForm.idType === 'nic' && (
                    <div className="jet-crm-form-grid jet-crm-form-grid--2 jet-crm-field--full">
                      <ImageUploadField
                        label="NIC front"
                        inputId="consignee-nic-front"
                        previewUrl={(consigneeForm.nicFront || consigneeForm.nicFrontUrl) ? getPreviewUrl(consigneeForm.nicFront || consigneeForm.nicFrontUrl) : ''}
                        onChoose={(e) => onImageChoose('consignee', 'nicFront', e.target.files?.[0])}
                      />
                      <ImageUploadField
                        label="NIC back"
                        inputId="consignee-nic-back"
                        previewUrl={(consigneeForm.nicBack || consigneeForm.nicBackUrl) ? getPreviewUrl(consigneeForm.nicBack || consigneeForm.nicBackUrl) : ''}
                        onChoose={(e) => onImageChoose('consignee', 'nicBack', e.target.files?.[0])}
                      />
                    </div>
                  )}
                </div>
                <footer className="jet-crm-form-footer">
                  <button type="submit" className="jet-btn jet-btn-primary" disabled={savingConsignee}>
                    {savingConsignee ? 'Saving…' : (customerPanelMode === 'edit' ? 'Save changes' : 'Create consignee')}
                  </button>
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={onResetConsignee}>
                    Clear form
                  </button>
                </footer>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
